// Selects named threat actors / groups relevant to a specific client.
// Ranks actors by:
//   1. profile match (does this actor target this vertical?)
//   2. signal match (do enrichment-derived signals — subdomain categories,
//      tech stack vendors, exposed services — match the actor's known
//      access vectors and TTPs?)
//   3. alerted (techStackSignals.namedActorAlerts list — direct alerts
//      from vendor-specific exploitation history)
//
// Output is consumed by the report and UI to show specific groups
// ("ALPHV/BlackCat actively targeted your VMware infrastructure during
// 2024 healthcare attacks") rather than abstract categories.

import threatActorIntel from '../../data/threatActorIntelligence.json'

export function selectRelevantActors({
  profileKey,
  subdomainCategories = new Set(),
  vendorMatches = [],
  namedActorAlerts = [],
  exposedServices = []
}) {
  const all = threatActorIntel.actors || []
  const ranked = []

  for (const actor of all) {
    let score = 0
    const reasons = []

    // 1. Profile match
    if (Array.isArray(actor.targetedProfiles) && profileKey && actor.targetedProfiles.includes(profileKey)) {
      score += 50
      reasons.push(`Targets ${profileKey} sector`)
    }

    // 2. Direct alert from tech-stack signal (highest weight)
    if (namedActorAlerts.includes(actor.id)) {
      score += 60
      const matchedVendor = (actor.signalIndicators?.techStack || []).find(t =>
        vendorMatches.some(v =>
          (v.key || '').replace(/_/g, ' ').toLowerCase() === t.toLowerCase() ||
          (v.aliases || []).some(a => a.toLowerCase() === t.toLowerCase())
        )
      )
      reasons.push(matchedVendor
        ? `Recently exploited ${matchedVendor} in known campaigns`
        : 'Vendor in this client\'s stack matches active targeting'
      )
    }

    // 3. Subdomain pattern overlap
    if (Array.isArray(actor.signalIndicators?.subdomainPatterns)) {
      const overlap = actor.signalIndicators.subdomainPatterns.filter(p =>
        Array.from(subdomainCategories).some(c => c.includes(p) || p.includes(c.replace(/_.*$/, '')))
      )
      if (overlap.length > 0) {
        score += 15 * overlap.length
        reasons.push(`Operational surface matches actor reconnaissance patterns: ${overlap.join(', ')}`)
      }
    }

    // 4. Exposed service overlap
    if (Array.isArray(actor.signalIndicators?.exposedServices) && exposedServices.length > 0) {
      const overlap = actor.signalIndicators.exposedServices.filter(s =>
        exposedServices.some(e => e.toLowerCase().includes(s.toLowerCase()))
      )
      if (overlap.length > 0) {
        score += 25 * overlap.length
        reasons.push(`Exposed services align with actor's known initial-access vectors: ${overlap.join(', ')}`)
      }
    }

    // 5. Confidence weight
    if (actor.confidence === 'high') score += 5
    if (actor.confidence === 'medium') score += 2

    if (score > 0) {
      ranked.push({ actor, score, reasons })
    }
  }

  ranked.sort((a, b) => b.score - a.score)
  return ranked
}

// Compact summary of the top N actors for advisor consumption.
export function summarizeTopActors(rankedActors, n = 5) {
  return rankedActors.slice(0, n).map(({ actor, score, reasons }) => ({
    id: actor.id,
    name: actor.name,
    type: actor.type,
    attribution: actor.attribution,
    motivations: actor.motivations,
    primaryAccessVectors: actor.primaryAccessVectors,
    recentNotableIncidents: actor.recentNotableIncidents,
    score,
    relevanceReasons: reasons,
    confidence: actor.confidence
  }))
}
